import {Moment} from 'moment';

import type {CalendarFilesByDate} from 'calendar/CalendarConstants';
import type {Referral} from 'constants/EntityStoreConstants';
import type {
  EntitySummary,
  UpdateUserMetadataPayload,
  UserMetadata,
  UserReferralModalsResponse,
} from 'constants/UserConstants';
import type {Organization, UserOrganizationRequestsResponse} from 'orgs/OrganizationConstants';
import {Request} from 'requests/RequestConstants';
import * as net from 'util/Net';
import type {BetterJQueryPromise} from 'util/Promises';
import * as TaskRunner from 'util/TaskRunner';

import {ProcessTaskForCalendarGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskForCalendarGraphQL';

const tzOffset = new Date().getTimezoneOffset() / -60;

export function getCurrentOrgs(): JQueryPromise<Array<Organization>> {
  return net.getJSON('/1/api/users/me/organizations/');
}

export function getOrgRequests(): JQueryPromise<UserOrganizationRequestsResponse> {
  return net.getJSON('/1/api/users/me/organization-requests/');
}

export function createInboundRequest(orgId: string): JQueryPromise<void> {
  return net.postJSON({
    url: '/1/api/users/me/organization-requests/',
    data: {organizationId: orgId},
  });
}

export function acceptOutboundRequest(orgId: string): JQueryPromise<void> {
  return net.postJSON({
    url: `/1/api/users/me/organization-requests/${orgId}/actions/accept`,
  });
}

export function rejectOutboundRequest(orgId: string): JQueryPromise<void> {
  return net.postJSON({
    url: `/1/api/users/me/organization-requests/${orgId}/actions/reject`,
  });
}

export function getCalendarRequests(
  startDate: Moment,
  endDate: Moment,
  {organizationId, teamId}: {organizationId?: string; teamId?: string} = {}
): BetterJQueryPromise<{
  [dateString: string]: Array<Request>;
}> {
  return TaskRunner.runTask({
    url: '/1/api/users/me/calendar/requests',
    dataType: 'json',
    data: {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      tzOffset,
      organizationId,
      teamId,
    },
  });
}

export function getCalendarProcessTasks(
  startDate: Moment,
  endDate: Moment,
  {organizationId, teamId}: {organizationId?: string; teamId?: string} = {}
): BetterJQueryPromise<{
  [dateString: string]: Array<ProcessTaskForCalendarGraphQL>;
}> {
  return TaskRunner.runTask({
    url: '/1/api/users/me/calendar/process-tasks',
    dataType: 'json',
    data: {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      tzOffset,
      organizationId,
      teamId,
    },
  });
}

export function getCalendarEntries(
  startDate: Moment,
  endDate: Moment,
  {organizationId, teamId}: {organizationId?: string; teamId?: string} = {}
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
): JQueryPromise<any> {
  return TaskRunner.runTask({
    url: '/1/api/users/me/calendar/entries',
    dataType: 'json',
    data: {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      tzOffset,
      organizationId,
      teamId,
    },
  });
}

export function getCalendarFiles(
  startDate: Moment,
  endDate: Moment,
  {organizationId, teamId}: {organizationId?: string; teamId?: string} = {}
): JQueryPromise<CalendarFilesByDate> {
  return TaskRunner.runTask({
    url: '/1/api/users/me/calendar/files',
    dataType: 'json',
    data: {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
      tzOffset,
      organizationId,
      teamId,
    },
  });
}

export function updateCalendarEntryTask(data: {
  completed: boolean;
  entryId: string;
  notePartProvenanceId: number;
}): JQueryPromise<void> {
  return net.postJSON({
    url: '/1/api/users/me/calendar/entries/actions/update-task',
    data,
  });
}

export function getReferrals(): JQueryPromise<{data: Array<Referral>}> {
  return net.getJSON('/1/api/users/me/referrals');
}

export function getPublicReferralId(email: string): JQueryPromise<{referralLinkId: string}> {
  return net.getJSON('/1/api/public-referrals', {email});
}

export function createPublicReferralId(email: string): JQueryPromise<{referralLinkId: string}> {
  return net.postJSON({url: '/1/api/public-referrals', data: {email}});
}

export function getMetadata(): JQueryPromise<UserMetadata> {
  return net.getJSON('/1/api/users/me/metadata');
}

export function updateMetadata(data: UpdateUserMetadataPayload): JQueryPromise<UserMetadata> {
  return net.patchJSON({url: '/1/api/users/me/metadata', data});
}

export function getReferralModals(): JQueryPromise<UserReferralModalsResponse> {
  return net.getJSON('/1/api/users/me/referral-modals');
}

export function acceptInstitutionTerms(userId: string): Promise<{}> {
  return net.postJSONPromise({url: `/1/api/users/${userId}:accept-institution-terms`});
}

export function fetch(userId: string): JQueryPromise<EntitySummary> {
  return net.getJSON(`/1/api/users/${userId}`);
}

export function suspendUsers(isSuspendedByUserId: {[userId: string]: boolean}): Promise<{}> {
  return net.postJSONPromise({url: `/1/api/users:bulk-suspend`, data: {isSuspendedByUserId}});
}

export function updateUser(
  userId: string,
  userUpdateInfo: {name: string; handle: string; email: string}
): Promise<{}> {
  return net.patchJSONPromise({url: `/1/api/users/${userId}`, data: userUpdateInfo});
}

export function userURL(userId: string): string {
  return `/users/${userId}`;
}
